import React, { useState, useEffect } from 'react';
import { Download, Search, Trash2, Plus, Edit2, GripVertical, CheckSquare, Square, RefreshCw } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../api';
import { MonthContext } from '../context/MonthContext';

const DEFAULT_COLS = [
  { id: 'select', label: '☑', width: '40px' },
  { id: 'actions', label: 'Actions', width: '100px' },
  { id: 'employee_name', label: 'Collaborateur', width: '200px' },
  { id: 'days', label: 'Jours', type: 'number', width: '100px' },
  { id: 'start_date', label: 'Date Début', width: '150px' },
  { id: 'end_date', label: 'Date Fin', width: '150px' },
  { id: 'month_year', label: 'Mois d\'imputation', width: '150px' },
  { id: 'processed_at', label: 'Importé le', type: 'datetime', width: '180px' }
];

const SortableHeader = ({ col, sortBy, sortDir, filters, setFilters, handleSort, setPage, allSelected, handleSelectAll, width, onResize }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: width || col.width,
    minWidth: width || col.width,
    maxWidth: width || col.width,
    padding: 0,
    verticalAlign: 'top',
    background: 'var(--surface)',
    borderBottom: '2px solid var(--border)',
    zIndex: transform ? 1 : 0,
    position: 'relative'
  };

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.pageX;
    const startWidth = parseInt(width || col.width || '150', 10);
    
    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.max(50, startWidth + (moveEvent.pageX - startX));
      onResize(`${newWidth}px`);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <th ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', minHeight: '48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', flexGrow: 1 }} onClick={() => handleSort(col.id)}>
          {col.id === 'select' ? (
            <input type="checkbox" checked={allSelected} onChange={handleSelectAll} onClick={(e) => e.stopPropagation()} />
          ) : (
            <>{col.label} {sortBy === col.id && (sortDir === 'asc' ? '↑' : '↓')}</>
          )}
        </div>
        {col.id !== 'select' && col.id !== 'actions' && (
          <div {...attributes} {...listeners} style={{ cursor: 'grab', padding: '2px', opacity: 0.5 }}><GripVertical size={14} /></div>
        )}
      </div>
      <div 
        onMouseDown={handleResizeMouseDown}
        style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', zIndex: 10, background: 'transparent' }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      />
    </th>
  );
};

const LeavesTable = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  
  const [page, setPage] = useState(1);
  const limit = 50; 
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  
  const [filters, setFilters] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const { selectedMonth } = React.useContext(MonthContext);

  const [activeCols, setActiveCols] = useState(() => {
    const saved = localStorage.getItem('leaves_cols_v1');
    return saved ? JSON.parse(saved) : DEFAULT_COLS.map(c => c.id);
  });

  const [colWidths, setColWidths] = useState(() => {
    const saved = localStorage.getItem('leaves_widths_v1');
    return saved ? JSON.parse(saved) : {};
  });

  const handleResizeColumn = (colId, newWidth) => {
    setColWidths(prev => {
      const next = { ...prev, [colId]: newWidth };
      localStorage.setItem('leaves_widths_v1', JSON.stringify(next));
      return next;
    });
  };

  const columns = activeCols.map(id => DEFAULT_COLS.find(c => c.id === id)).filter(Boolean);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setActiveCols((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newArray = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem('leaves_cols_v1', JSON.stringify(newArray));
        return newArray;
      });
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, search, sortBy, sortDir, month_year: selectedMonth, ...filters });
      const res = await api.get(`/leaves?${params.toString()}`);
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    const timer = setTimeout(() => { loadData(); }, 300);
    return () => clearTimeout(timer);
  }, [page, search, sortBy, sortDir, filters, selectedMonth]);

  const handleSort = (field) => {
    if (field === 'select' || field === 'actions') return;
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(data.map(d => d.id));
    else setSelectedIds([]);
  };

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const handleDeleteMany = async () => {
    if (!window.confirm(`Supprimer ${selectedIds.length} congé(s) ?`)) return;
    try {
      await api.delete(`/leaves?ids=${selectedIds.join(',')}`);
      setSelectedIds([]);
      loadData();
    } catch (err) {
      alert("Erreur lors de la suppression.");
    }
  };

  const exportExcel = () => {
    const params = new URLSearchParams({ search, ...filters });
    api.get(`/leaves/export?${params.toString()}`, { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'export_conges.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }).catch(() => alert("Erreur d'exportation"));
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({ ...row });
  };

  const saveEdit = async (id) => {
    try {
      if (id === 'new') {
        await api.post('/leaves', editForm);
      } else {
        await api.put(`/leaves/${id}`, editForm);
      }
      setEditingId(null);
      loadData();
    } catch (e) {
      alert('Erreur de modification');
    }
  };

  const handleManualAdd = () => {
    const newId = 'new';
    setEditingId(newId);
    setEditForm({ employee_name: '', days: 1, start_date: '', end_date: '', month_year: '' });
    setData([{ id: newId }, ...data]); // Insert temporary row
  };

  const renderCellContent = (col, row, isEditing) => {
    if (col.id === 'select') {
      if (row.id === 'new') return null;
      return <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => handleSelect(row.id)} />;
    }
    if (col.id === 'actions') {
      if (isEditing) {
        return (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-icon" onClick={() => saveEdit(row.id)} style={{ color: 'var(--success)' }}>✔</button>
            <button className="btn-icon" onClick={() => { setEditingId(null); if(row.id === 'new') loadData(); }} style={{ color: 'var(--danger)' }}>✖</button>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <span className="action-link" onClick={() => startEdit(row)}><Edit2 size={16} /></span>
          <span className="action-link delete" onClick={async () => {
             if(!window.confirm('Supprimer ?')) return;
             await api.delete(`/leaves?ids=${row.id}`);
             loadData();
          }}><Trash2 size={16} /></span>
        </div>
      );
    }
    
    if (isEditing) {
      return <input className="input-field" type={col.type === 'number' ? 'number' : 'text'} step={col.type==='number'?'0.5':undefined} value={editForm[col.id] || ''} onChange={e => setEditForm({...editForm, [col.id]: e.target.value})} style={{ padding:'0.25rem', width: '100%' }} />;
    }

    let val = row[col.id];
    if (col.id === 'month_year' && val && !isEditing) {
      const [y, m] = val.split('-');
      if (y && m) {
         const dateObj = new Date(parseInt(y), parseInt(m) - 1, 1);
         const mName = dateObj.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
         return mName.charAt(0).toUpperCase() + mName.slice(1);
      }
    }
    if (col.type === 'datetime' && val) return new Date(val).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    return val || '-';
  };

  const renderPagination = () => (
    <div className="pagination">
      <div>Affichage de {(page - 1) * limit + (total > 0 ? 1 : 0)} à {Math.min(page * limit, total)} sur {total}</div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button className="btn-icon" disabled={page === 1} onClick={() => setPage(page - 1)}>&lt;</button>
        <button className="btn-icon" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>&gt;</button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div className="glass-panel data-table-wrapper" style={{ overflowX: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div className="table-toolbar" style={{ flexWrap: 'wrap', padding: '1rem' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--text-secondary)' }} />
            <input 
              type="text" placeholder="Recherche globale..." className="input-field" 
              style={{ paddingLeft: '2.2rem', width: '250px' }}
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="btn" style={{ background: 'var(--brand)', color: 'white', border: 'none', height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={handleManualAdd}>
              <Plus size={16} /> Ajouter
            </button>
            <button className="btn btn-danger" style={{ opacity: selectedIds.length === 0 ? 0.5 : 1, height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }} disabled={selectedIds.length === 0} onClick={handleDeleteMany}>
              <Trash2 size={16} /> Supprimer ({selectedIds.length})
            </button>
            <button className="btn" style={{ background: '#8b5cf6', color: 'white', border: 'none', height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={loadData}>
              <RefreshCw size={16} /> Actualiser
            </button>
            <button className="btn" style={{ background: 'var(--success)', color: 'white', border: 'none', height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={exportExcel}>
              <Download size={16} /> Excel
            </button>
          </div>
        </div>

        {renderPagination()}

        <div className="table-container" style={{ overflowX: 'auto', paddingBottom: '1rem' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <table style={{ minWidth: 'max-content', width: '100%' }}>
              <thead>
                <SortableContext items={activeCols} strategy={horizontalListSortingStrategy}>
                  <tr>
                    {columns.map(col => (
                      <SortableHeader 
                        key={col.id} col={col} width={colWidths[col.id] || col.width}
                        onResize={(newWidth) => handleResizeColumn(col.id, newWidth)}
                        sortBy={sortBy} sortDir={sortDir} filters={filters} setFilters={setFilters} 
                        handleSort={handleSort} setPage={setPage}
                        allSelected={data.length > 0 && selectedIds.length === data.filter(d=>d.id!=='new').length}
                        handleSelectAll={handleSelectAll}
                      />
                    ))}
                  </tr>
                </SortableContext>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={columns.length} style={{ textAlign:'center' }}>Chargement...</td></tr>
                ) : data.length === 0 ? (
                  <tr><td colSpan={columns.length} style={{ textAlign:'center' }}>Aucun congé trouvé.</td></tr>
                ) : (
                  data.map(row => (
                    <tr key={row.id}>
                      {columns.map(col => (
                        <td key={col.id} style={{ whiteSpace: 'nowrap', width: colWidths[col.id] || col.width, minWidth: colWidths[col.id] || col.width, maxWidth: colWidths[col.id] || col.width, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {col.id === 'select' && row.id !== 'new' && (
                            <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => handleSelect(row.id)} />
                          )}
                          {col.id !== 'select' && renderCellContent(col, row, editingId === row.id)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default LeavesTable;
